import { EPaymentProvider, IPlan } from '@/models/checkout.model';

export const GTM_ECOMMERCE_EVENTS = {
  ADD_TO_CART: 'add_to_cart',
  BEGIN_CHECKOUT: 'begin_checkout',
  ADD_PAYMENT_INFO: 'add_payment_info',
} as const;

export const GTM_INTERVAL_UNIT = {
  YEAR: 'year',
  MONTH: 'month',
  LIFETIME: 'lifetime',
  SIX_MONTH: '6 month',
} as const;

export const GTM_STATUS = {
  ACTIVE: 'active',
  ACTIVE_COUPON: 'active coupon',
  LIFETIME_PREMIUM: 'lifetime premium',
} as const;

export const GTM_PURCHASE_BEHAVIOR = {
  NON_TRIAL: 'non-trial purchase',
  POST_TRIAL_IMMEDIATE: 'post-trial immediate purchase',
} as const;

export const GTM_PAYMENT_TYPE = {
  STRIPE: 'stripe',
  PAYPAL: 'paypal',
} as const;

export const GTM_ITEM_BRAND = 'BlockSite' as const;

export const GTM_ITEM_CATEGORY = {
  SUBSCRIPTION: 'BlockSite subscription',
  IMAGE_PACK: 'Image pack',
} as const;

export type GtmEcommerceEventName =
  (typeof GTM_ECOMMERCE_EVENTS)[keyof typeof GTM_ECOMMERCE_EVENTS];

export type GtmIntervalUnit =
  (typeof GTM_INTERVAL_UNIT)[keyof typeof GTM_INTERVAL_UNIT];

export type GtmStatus = (typeof GTM_STATUS)[keyof typeof GTM_STATUS];

export type GtmPurchaseBehavior =
  (typeof GTM_PURCHASE_BEHAVIOR)[keyof typeof GTM_PURCHASE_BEHAVIOR];

export type GtmPaymentType = Lowercase<`${EPaymentProvider}`>;

export type GtmItemCategory =
  (typeof GTM_ITEM_CATEGORY)[keyof typeof GTM_ITEM_CATEGORY];

export interface GtmAddress {
  readonly first_name?: string;
  readonly last_name?: string;
}

export interface GtmEcommerceItem {
  readonly item_name: string;
  readonly item_brand: typeof GTM_ITEM_BRAND;
  readonly item_category: GtmItemCategory;
  readonly price: number;
  readonly quantity: 1;
  readonly coupon?: string;
  readonly discount?: number;
}

export interface GtmEcommerceData {
  readonly currency: string;
  readonly value: number;
  readonly coupon?: string;
  readonly discount?: number;
  readonly items: readonly GtmEcommerceItem[];
}

export interface GtmEcommerceEventPayload {
  readonly event: GtmEcommerceEventName;
  readonly user_id: string;
  readonly plan_name: string;
  readonly price_per_month: string;
  readonly price_before_discount: string;
  readonly trial_period_in_days?: number;
  readonly purchase_behavior: GtmPurchaseBehavior;
  readonly status: GtmStatus;
  readonly interval_count: number;
  readonly interval_unit: GtmIntervalUnit;
  readonly payment_type?: GtmPaymentType;
  readonly email_address?: string;
  readonly phone_number?: string;
  readonly address?: GtmAddress;
  readonly ecommerce: GtmEcommerceData;
}

export interface GtmEcommerceParams {
  readonly plan: IPlan;
  readonly userId: string;
  readonly status: GtmStatus;
  readonly purchaseBehavior: GtmPurchaseBehavior;
  readonly itemCategory?: GtmItemCategory;
  readonly paymentType?: GtmPaymentType;
  readonly email?: string;
  readonly phoneNumber?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly couponCode?: string;
  readonly discountAmount?: number;
}
